<script setup lang="ts">
import { computed } from 'vue';
import { BaseButton, BaseIcon, FieldInput } from '@magma-app/magma-lapilli';

const props = defineProps<{
  phoneNumber: string;
  code: string;
  codeError: boolean;
  canResend: boolean;
  timerBeforeResend: number;
  isResending: boolean;
  isLoading?: boolean;
  primaryColor?: string;
  rgbColor?: string;
}>();

const emit = defineEmits<{
  (e: 'update:code', value: string): void;
  (e: 'resend'): void;
  (e: 'submit'): void;
  (e: 'back'): void;
}>();

const codeModel = computed({
  get: () => props.code,
  set: (value: string) => emit('update:code', value),
});

const onResend = () => {
  if (props.isLoading) {
    return;
  }

  emit('resend');
};

const onSubmit = () => {
  if (props.isLoading || codeModel.value === '' || codeModel.value.length !== 6) {
    return;
  }

  emit('submit');
};

const onBack = () => {
  emit('back');
};
</script>

<template>
  <div class="relative h-full flex-1">
    <h3 class="text-mgm-txt-lg font-medium text-foreground-emphasis mb-4">
      {{ $t('helpeeCampaignSignUpView.phoneVerification') }}
    </h3>

    <p class="text-foreground-default text-mgm-txt-sm mb-4">
      {{ $t('helpeeCampaignSignUpView.code.text') }}
      {{ phoneNumber }} (<span
        role="button"
        class="underline"
        @click="onBack"
        >{{ $t('helpeeCampaignSignUpView.code.modifyNumber') }}</span
      >)
    </p>

    <FieldInput
      id="code"
      v-model="codeModel"
      type="number"
      autocomplete="one-time-code"
      max-length="6"
      min-length="6"
      data-cy="code"
      class="mt-4"
      :locale="$i18n.locale"
      :label="$t('helpeeCampaignSignUpView.codeLabel')"
      required
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
      :validation-disabled="true"
    />

    <span
      v-if="codeError"
      class="text-mgm-txt-xs text-background-danger-emphasis"
    >
      {{ $t('helpeeCampaignSignUpView.codeWrong') }}
    </span>
    <div class="flex items-center flex-col mt-4">
      <p class="text-foreground-default text-mgm-txt-sm mb-1">
        {{ $t('helpeeCampaignSignUpView.noReceive') }}
      </p>
      <p
        v-if="!canResend"
        class="text-foreground-default text-mgm-txt-sm mb-1 font-medium"
      >
        <template v-if="isResending">
          {{ $t('generic.loading') }}
        </template>
        <template v-else>
          {{ $t('helpeeCampaignSignUpView.code.resend') }}
          {{ timerBeforeResend }}
          {{ $t('helpeeCampaignSignUpView.code.seconds') }}
        </template>
      </p>
      <button
        v-else
        class="text-foreground-brand-default text-mgm-txt-sm flex items-center"
        @click="onResend"
      >
        <BaseIcon
          icon="refresh-cw"
          class="mr-2 -scale-x-100"
          color="rgb(68 76 231)"
        />
        <span class="underline">{{
          $t('helpeeCampaignSignUpView.resendCode')
        }}</span>
      </button>
    </div>

    <BaseButton
      data-cy="signup-next"
      class="mt-8 w-full"
      :disabled="codeModel === '' || codeModel.length !== 6 || isLoading"
      :is-loading="isLoading"
      :text="$t('generic.next')"
      size="md"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
      @click="onSubmit"
    />
  </div>
</template>
